import { Modal } from 'react-bootstrap';
import { PartnerStore } from '../models/PartnerStore';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { PartnerStoreRequest } from '../models/PartnerStoreRequest';
import { listStores } from '../../plans';
import {
  createPartnerStore,
  updatePartnerStore,
} from '../services/PartnerStoreCRUD';
import { toast } from 'react-toastify';

interface ModalPartnerStoreFormProps {
  visible: boolean;
  onClose: () => void;
  onSubmitted: (partnerStore: PartnerStore) => void;
  partnerStore?: PartnerStore;
}

export default function ModalPartnerStoreForm({
  visible,
  onClose,
  onSubmitted,
  partnerStore,
}: ModalPartnerStoreFormProps) {
  const intl = useIntl();
  const [stores, setStores] = useState<Array<{ id: number; name: string }>>([]);
  const [partnerStoreRequest, setParnerStoreRequest] =
    useState<PartnerStoreRequest | null>({
      store: 1,
      name: '',
      description: '',
      benefit: '',
      href: '',
      image: null,
    });

  async function fetchStores() {
    try {
      const { data: stores } = await listStores();

      setStores(stores);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  }

  useEffect(() => {
    fetchStores();
  }, []);

  function onSubmit(partnerStoreRequest: PartnerStoreRequest) {
    if (partnerStore?.id) {
      onUpdatePartnerStore(partnerStoreRequest);
    } else {
      onCreatePartnerStore(partnerStoreRequest);
    }
  }

  async function onUpdatePartnerStore(
    partnerStoreRequest: PartnerStoreRequest
  ) {
    try {
      const { data } = await updatePartnerStore(
        partnerStore?.id,
        partnerStoreRequest
      );

      onSubmitted(data);
    } catch (error) {
      console.error('Error updating partner store:', error);
      toast.error(
        intl.formatMessage(
          { id: 'partnerStores.errorUpdatingPartnerStore' },
          {
            name: partnerStoreRequest.name,
          }
        )
      );
    }
  }

  async function onCreatePartnerStore(
    partnerStoreRequest: PartnerStoreRequest
  ) {
    try {
      const { data } = await createPartnerStore(partnerStoreRequest);

      onSubmitted(data);
    } catch (error) {
      console.error('Error creating partner store:', error);
      toast.error(
        intl.formatMessage(
          { id: 'partnerStores.errorCreatingPartnerStore' },
          {
            name: partnerStoreRequest.name,
          }
        )
      );
    }
  }

  useEffect(() => {
    setParnerStoreRequest({
      store: partnerStore?.store?.id || 1,
      name: partnerStore?.name || '',
      description: partnerStore?.description || '',
      benefit: partnerStore?.benefit || '',
      href: partnerStore?.href || '',
      image: null,
      id: partnerStore?.id || undefined,
      coupon: partnerStore?.coupon || '',
    });
  }, [partnerStore]);

  return (
    <Modal
      show={visible}
      onHide={onClose}
      backdrop="static"
      centered
      keyboard={false}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(partnerStoreRequest);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {partnerStore?.id
              ? intl.formatMessage({ id: 'partnerStores.editPartnerStore' })
              : intl.formatMessage({ id: 'partnerStores.addParnerStore' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group mb-4">
            <label className="form-label" htmlFor="store">
              {intl.formatMessage({ id: 'partnerStores.store' })}
            </label>
            <select
              className="form-select"
              id="store"
              required
              value={partnerStoreRequest?.store || ''}
              onChange={(e) =>
                setParnerStoreRequest({
                  ...partnerStoreRequest,
                  store: parseInt(e.target.value, 10),
                })
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'partnerStores.selectStore' })}
              </option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group mb-4">
            <label className="form-label" htmlFor="storeName">
              {intl.formatMessage({ id: 'partnerStores.storeName' })}
            </label>
            <input
              type="text"
              className="form-control"
              id="storeName"
              required
              value={partnerStoreRequest?.name || ''}
              onChange={(e) =>
                setParnerStoreRequest({
                  ...partnerStoreRequest,
                  name: e.target.value,
                })
              }
            />
          </div>
          <div className="form-group mb-4">
            <label className="form-label" htmlFor="storeDescription">
              {intl.formatMessage({ id: 'partnerStores.storeDescription' })}
            </label>
            <input
              type="text"
              className="form-control"
              id="storeDescription"
              required
              value={partnerStoreRequest?.description || ''}
              onChange={(e) =>
                setParnerStoreRequest({
                  ...partnerStoreRequest,
                  description: e.target.value,
                })
              }
            />
          </div>
          <div className="form-group mb-4">
            <label className="form-label" htmlFor="storeBenefit">
              {intl.formatMessage({ id: 'partnerStores.storeBenefit' })}
            </label>
            <textarea
              rows={3}
              className="form-control"
              id="storeBenefit"
              required
              value={partnerStoreRequest?.benefit || ''}
              onChange={(e) =>
                setParnerStoreRequest({
                  ...partnerStoreRequest,
                  benefit: e.target.value,
                })
              }
            />
          </div>
          <div className="form-group mb-4">
            <label className="form-label" htmlFor="storeHref">
              {intl.formatMessage({ id: 'partnerStores.storeHref' })}
            </label>
            <input
              type="url"
              className="form-control"
              id="storeHref"
              required
              value={partnerStoreRequest?.href || ''}
              onChange={(e) =>
                setParnerStoreRequest({
                  ...partnerStoreRequest,
                  href: e.target.value,
                })
              }
            />
          </div>
          <div className="form-group mb-4">
            <label className="form-label" htmlFor="storeCoupon">
              {intl.formatMessage({ id: 'partnerStores.storeCoupon' })}
            </label>
            <input
              type="text"
              className="form-control"
              id="storeCoupon"
              value={partnerStoreRequest?.coupon || ''}
              onChange={(e) =>
                setParnerStoreRequest({
                  ...partnerStoreRequest,
                  coupon: e.target.value,
                })
              }
            />
          </div>
          <div className="form-group mb-4">
            <label className="form-label" htmlFor="storeImage">
              {intl.formatMessage({ id: 'partnerStores.storeImage' })}
            </label>
            {partnerStore?.image ? (
              <div className="mb-3">
                <img
                  src={partnerStore.image}
                  alt={partnerStore.name}
                  className="img-thumbnail"
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>
            ) : (
              <input
                type="file"
                className="form-control"
                id="storeImage"
                accept="image/*"
                required
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setParnerStoreRequest({
                      ...partnerStoreRequest,
                      image: file,
                    });
                  }
                }}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {intl.formatMessage({ id: 'common.cancel' })}
          </button>
          <button type="submit" className="btn btn-primary">
            {partnerStore?.id
              ? intl.formatMessage({ id: 'common.save' })
              : intl.formatMessage({ id: 'partnerStores.addParnerStore' })}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
